import React from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { fontRouter } from "@editor/trpc/font";
import { GOOGLE_FONT_OPTIONS } from "@editor/utils/font";

import { Combobox } from "@replo/design-system/components/combobox/Combobox";
import { useCombobox } from "@replo/design-system/components/combobox/hooks/useCombobox";
import { MenuItem } from "@replo/design-system/components/menu/MenuItem";
import { skipToken } from "@tanstack/react-query";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";

interface FontSelectorProps {
  fontFamily: string | null;
  onSelect: (fontValue: string) => void;
  placeholder: string;
}

const ChevronIcon = () => {
  const { open } = useCombobox();
  return open ? (
    <ChevronUp size={12} className="text-muted" />
  ) : (
    <ChevronDown size={12} className="text-muted" />
  );
};

const FontSelector: React.FC<FontSelectorProps> = ({
  fontFamily,
  onSelect,
  placeholder,
}) => {
  const projectId = useCurrentProjectId();

  const { data: projectFonts = [] } = fontRouter.getFonts.useQuery(
    projectId ? { projectId } : skipToken,
  );

  const [fontPreviewErrors, setFontPreviewErrors] = React.useState<
    Record<string, boolean>
  >({});

  const [displayValue, setDisplayValue] = useOverridableState(
    fontFamily ?? "",
    onSelect,
  );

  // Normalize font family for display
  const normalizedFontFamily = displayValue
    ? displayValue.split(",")[0]?.trim().replace(/["']/g, "")
    : null;

  const uniqueProjectFonts = Array.from(
    new Set(projectFonts.map((font) => font.name)),
  );

  const projectFontOptions = uniqueProjectFonts.map((fontName) => ({
    value: fontName,
    label: fontName,
    displayValue: fontName,
    groupTitle: "Project Fonts",
  }));

  const googleFontOptions = GOOGLE_FONT_OPTIONS.map((font) => ({
    ...font,
    displayValue: font.label,
    groupTitle: "Google Fonts",
    label: !fontPreviewErrors[font.value ?? ""] ? (
      <MenuItem
        variant="default"
        size="sm"
        selected={
          typeof displayValue === "string"
            ? displayValue?.includes(font.value ?? "")
            : false
        }
      >
        <img
          src={`/images/font-previews/${font.value?.replace(/\s+/g, "_")}.webp`}
          alt={font.label}
          width={182}
          height={16}
          loading="lazy"
          onError={() =>
            setFontPreviewErrors((prev) => ({
              ...prev,
              [font.value ?? ""]: true,
            }))
          }
        />
      </MenuItem>
    ) : (
      font.label
    ),
  }));

  const fontOptions = [...projectFontOptions, ...googleFontOptions];

  return (
    <div className="w-full overflow-hidden">
      <Combobox.Root
        options={fontOptions.filter((opt) => opt.value !== null)}
        value={normalizedFontFamily ?? ""}
        onChange={setDisplayValue}
      >
        <Combobox.Trigger>
          <Combobox.SelectionButton
            layoutClassName="w-full"
            title={normalizedFontFamily ?? placeholder}
            isPlaceholder={!normalizedFontFamily}
            size="sm"
            endEnhancer={<ChevronIcon />}
          />
        </Combobox.Trigger>
        <Combobox.Popover align="center" layoutClassName="w-[220px] h-[416px]">
          <Combobox.Content
            areOptionsSearchable
            inputPlaceholder="Search fonts"
            emptySearchMessage="No matches found"
          />
        </Combobox.Popover>
      </Combobox.Root>
    </div>
  );
};

export default FontSelector;
